/**
 * Type guards for safely accessing properties on UnitInfoInterface union types.
 * These guards help TypeScript narrow the union type without needing `as any` casts.
 */

import type {
  UnitInfoInterface,
  UnitInfoBaseInterface,
  DisplayBaseInterface,
} from 'store/units/types';

/**
 * Type guard to check if a unitInfo has modelName property.
 * Returns true for unit types that extend UnitInfoBaseInterface.
 */
export const hasModelName = (
  unitInfo: UnitInfoInterface,
): unitInfo is UnitInfoInterface & UnitInfoBaseInterface => {
  return 'modelName' in unitInfo;
};

/**
 * Type guard to check if a unitInfo has a display property with displayRiskLevel.
 * Returns true for unit types that extend DisplayBaseInterface.
 */
export const hasDisplayRiskLevel = (
  unitInfo: UnitInfoInterface,
): unitInfo is UnitInfoInterface &
  DisplayBaseInterface<{ displayRiskLevel?: boolean }> => {
  return (
    'display' in unitInfo &&
    unitInfo.display !== null &&
    typeof unitInfo.display === 'object'
  );
};

/**
 * Safely gets the modelName from a unitInfo, returning a fallback if not present.
 */
export const getModelName = (
  unitInfo: UnitInfoInterface,
  fallback = '',
): string => {
  return hasModelName(unitInfo) ? unitInfo.modelName : fallback;
};

/**
 * Safely gets the displayRiskLevel from a unitInfo, returning undefined if not present.
 */
export const getDisplayRiskLevel = (
  unitInfo: UnitInfoInterface,
): boolean | undefined => {
  if (hasDisplayRiskLevel(unitInfo)) {
    return unitInfo.display.displayRiskLevel;
  }
  return undefined;
};
